'use client';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  memo,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { fetchActiveOrders, fetchTradeHistory } from '@/lib/api/history/client';
import { ACTIVE_ORDERS_QK, TRADE_HISTORY_QK } from '@/lib/api/history/hooks';
import type { ActiveOrderPageResponse, TradeHistoryPageResponse } from '@/lib/api/history/models';
import { useUserBalances } from '@/lib/api/user-balances/hooks';
import type { UserBalancesResult } from '@/lib/api/user-balances/models';
import { AuthContext } from '@/lib/auth/AuthProvider';
import { useActivityHistoryWebSocket } from '@/lib/hooks/useActivityHistoryWebSocket';
import { useOrderHistoryWebSocket } from '@/lib/hooks/useOrderHistoryWebSocket';
import { useOrderFormStore } from '@/lib/store/order-form-store';
import { MarketItem } from '@/lib/types/markets/market';

// Data structure for a single outcome
type OutcomeTradingData = {
  tradeHistory: TradeHistoryPageResponse | undefined;
  activeOrders: ActiveOrderPageResponse | undefined;
  isLoading: boolean;
  hasHistory: boolean | undefined;
  hasOrders: boolean | undefined;
};

// Context value type
type TradingDataContextValue = {
  // Get data for specific outcome
  getTradeHistory: (outcomeId: string) => TradeHistoryPageResponse | undefined;
  getActiveOrders: (outcomeId: string) => ActiveOrderPageResponse | undefined;
  getBalances: () => UserBalancesResult | undefined;

  // Get full outcome data
  getOutcomeData: (outcomeId: string) => OutcomeTradingData;

  // Check loading state
  isLoading: (outcomeId: string) => boolean;

  // Manually fetch data for outcome
  fetchOutcomeData: (outcomeId: string) => Promise<void>;

  // Get outcomes array for positions calculation
  outcomes: MarketItem[];
};

const TradingDataContext = createContext<TradingDataContextValue | null>(null);

type TradingDataProviderProps = {
  children: ReactNode;
  outcomes: MarketItem[];
  eventId: string;
};

/**
 * TradingDataProvider - manages trading data (trade history, active orders, balances)
 * - Fetches data only for selectedOutcomeId (lazy loading)
 * - Shares data between components via context
 * - No automatic refetching or prefetching
 */
const TradingDataProviderInner = ({ children, outcomes, eventId }: TradingDataProviderProps) => {
  // Use useContext directly to avoid throwing error if AuthProvider is not yet initialized
  // This component is always rendered within RootProviders which includes AuthProvider
  // But during SSR or initial render, AuthProvider might not be ready yet
  const authContext = useContext(AuthContext);
  const isAuthenticated = authContext?.isAuthenticated ?? false;
  const queryClient = useQueryClient();
  const selectedOutcomeId = useOrderFormStore.use.selectedOutcomeId();

  // Track which outcomes have been loaded
  const loadedOutcomesRef = useRef<Set<string>>(new Set());

  // Collect all eventTokenIds from all outcomes for balances
  const allEventTokenIds = useMemo(() => {
    const ids: string[] = [];
    for (const outcome of outcomes) {
      const resolutions = outcome.r;
      const yesResolution = resolutions?.find((r) => r.isPositive === true);
      const noResolution = resolutions?.find((r) => r.isPositive === false);
      if (yesResolution?.id) ids.push(yesResolution.id);
      if (noResolution?.id) ids.push(noResolution.id);
    }
    return ids.join(',');
  }, [outcomes]);

  // Fetch balances for all outcomes at once (shared data)
  const { data: balancesData } = useUserBalances({
    eventTokenIds: allEventTokenIds || '',
    page: 0,
    size: 100,
  });

  // Connect to order history WebSocket for real-time active orders updates
  // Updates are applied directly to React Query cache via setQueryData
  useOrderHistoryWebSocket(undefined, {
    autoConnect: true,
  });

  // Connect to activity history WebSocket for real-time trade history updates
  // Updates trade history when user's orders are matched
  useActivityHistoryWebSocket(eventId, undefined, {
    autoConnect: true,
  });

  // Determine which outcome to fetch data for
  // Priority: selectedOutcomeId > first outcome (for initial mount)
  const outcomeToFetch = selectedOutcomeId || outcomes[0]?.id;

  // Track if we've loaded initial data on mount
  const hasLoadedInitialRef = useRef(false);

  // Load data for first outcome on mount if no selectedOutcomeId
  useEffect(() => {
    if (!hasLoadedInitialRef.current && !selectedOutcomeId && outcomes[0]?.id && isAuthenticated) {
      hasLoadedInitialRef.current = true;
      // Data will be loaded automatically via useQuery with outcomeToFetch
    }
  }, [selectedOutcomeId, outcomes, isAuthenticated]);

  // Fetch trade history for selected outcome (lazy loading)
  const { data: tradeHistoryData, isLoading: isLoadingTradeHistory } =
    useQuery<TradeHistoryPageResponse>({
      queryKey: [TRADE_HISTORY_QK, outcomeToFetch],
      queryFn: async () => {
        if (!isAuthenticated || !outcomeToFetch)
          throw new Error('Unauthorized or missing outcomeId');
        return fetchTradeHistory(outcomeToFetch);
      },
      enabled: isAuthenticated && Boolean(outcomeToFetch),
      staleTime: Infinity, // Never refetch automatically
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    });

  // Fetch active orders for selected outcome (lazy loading)
  const { data: activeOrdersData, isLoading: isLoadingActiveOrders } =
    useQuery<ActiveOrderPageResponse>({
      queryKey: [ACTIVE_ORDERS_QK, outcomeToFetch],
      queryFn: async () => {
        if (!isAuthenticated || !outcomeToFetch)
          throw new Error('Unauthorized or missing outcomeId');
        return fetchActiveOrders({ eventOutcomeId: outcomeToFetch, sort: ['asc'] });
      },
      enabled: isAuthenticated && Boolean(outcomeToFetch),
      staleTime: Infinity, // Never refetch automatically
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    });

  // Track loaded outcomes
  useEffect(() => {
    if (outcomeToFetch && (tradeHistoryData || activeOrdersData)) {
      loadedOutcomesRef.current.add(outcomeToFetch);
    }
  }, [outcomeToFetch, tradeHistoryData, activeOrdersData]);

  // Manual fetch function for specific outcome
  const fetchOutcomeData = useCallback(
    async (outcomeId: string) => {
      if (!isAuthenticated || !outcomeId) return;

      // Fetch both trade history and active orders in parallel
      await Promise.all([
        queryClient.fetchQuery({
          queryKey: [TRADE_HISTORY_QK, outcomeId],
          queryFn: () => fetchTradeHistory(outcomeId),
        }),
        queryClient.fetchQuery({
          queryKey: [ACTIVE_ORDERS_QK, outcomeId],
          queryFn: () => fetchActiveOrders({ eventOutcomeId: outcomeId, sort: ['asc'] }),
        }),
      ]);

      loadedOutcomesRef.current.add(outcomeId);
    },
    [isAuthenticated, queryClient],
  );

  const contextValue = useMemo((): TradingDataContextValue => {
    // Build data map from React Query cache
    const getOutcomeDataFromCache = (outcomeId: string): OutcomeTradingData => {
      const tradeHistoryQuery = queryClient.getQueryData<TradeHistoryPageResponse>([
        TRADE_HISTORY_QK,
        outcomeId,
      ]);
      const activeOrdersQuery = queryClient.getQueryData<ActiveOrderPageResponse>([
        ACTIVE_ORDERS_QK,
        outcomeId,
      ]);

      const tradeHistory = tradeHistoryQuery;
      const activeOrders = activeOrdersQuery;

      return {
        tradeHistory,
        activeOrders,
        isLoading: false, // We don't track loading for cached data
        hasHistory: tradeHistory ? tradeHistory.content.length > 0 : undefined,
        hasOrders: activeOrders ? activeOrders.items.length > 0 : undefined,
      };
    };
    return {
      getTradeHistory: (outcomeId: string) => {
        const data = getOutcomeDataFromCache(outcomeId);
        return data.tradeHistory;
      },
      getActiveOrders: (outcomeId: string) => {
        const data = getOutcomeDataFromCache(outcomeId);
        return data.activeOrders;
      },
      getBalances: () => balancesData,
      getOutcomeData: (outcomeId: string) => {
        // If this is the currently selected outcome, use live query data
        if (outcomeId === outcomeToFetch) {
          return {
            tradeHistory: tradeHistoryData,
            activeOrders: activeOrdersData,
            isLoading: isLoadingTradeHistory || isLoadingActiveOrders,
            hasHistory: tradeHistoryData ? tradeHistoryData.content.length > 0 : undefined,
            hasOrders: activeOrdersData ? activeOrdersData.items.length > 0 : undefined,
          };
        }
        // Otherwise, get from cache
        return getOutcomeDataFromCache(outcomeId);
      },
      isLoading: (outcomeId: string) => {
        if (outcomeId === outcomeToFetch) {
          return isLoadingTradeHistory || isLoadingActiveOrders;
        }
        return false; // Cached data is not loading
      },
      fetchOutcomeData,
      outcomes,
    };
  }, [
    balancesData,
    outcomeToFetch,
    tradeHistoryData,
    activeOrdersData,
    isLoadingTradeHistory,
    isLoadingActiveOrders,
    queryClient,
    fetchOutcomeData,
    outcomes,
  ]);

  return <TradingDataContext.Provider value={contextValue}>{children}</TradingDataContext.Provider>;
};

// CRITICAL FIX: Memoize the provider to prevent re-renders when parent re-renders with new outcomes array
// Custom comparison: only re-render if eventId changes or outcomes IDs change
export const TradingDataProvider = memo(TradingDataProviderInner, (prevProps, nextProps) => {
  // Return true if props are equal (skip re-render)
  if (prevProps.eventId !== nextProps.eventId) return false;

  // Compare outcomes by IDs (not by reference)
  const prevIds = prevProps.outcomes
    .map((o) => o.id)
    .sort()
    .join(',');
  const nextIds = nextProps.outcomes
    .map((o) => o.id)
    .sort()
    .join(',');

  return prevIds === nextIds; // true = skip re-render
});

/**
 * Hook to get trading data for a specific outcome
 */
export function useTradingData(outcomeId: string): OutcomeTradingData {
  const context = useContext(TradingDataContext);

  if (!context) {
    return {
      tradeHistory: undefined,
      activeOrders: undefined,
      isLoading: true,
      hasHistory: undefined,
      hasOrders: undefined,
    };
  }

  return context.getOutcomeData(outcomeId);
}

/**
 * Hook to get trade history for a specific outcome
 */
export function useTradingTradeHistory(outcomeId: string): TradeHistoryPageResponse | undefined {
  const context = useContext(TradingDataContext);
  return context?.getTradeHistory(outcomeId);
}

/**
 * Hook to get active orders for a specific outcome
 */
export function useTradingActiveOrders(outcomeId: string): ActiveOrderPageResponse | undefined {
  const context = useContext(TradingDataContext);
  return context?.getActiveOrders(outcomeId);
}

/**
 * Hook to get balances data (shared across all outcomes)
 */
export function useTradingBalances(): UserBalancesResult | undefined {
  const context = useContext(TradingDataContext);
  return context?.getBalances();
}

/**
 * Hook to manually fetch data for an outcome
 */
export function useFetchTradingData() {
  const context = useContext(TradingDataContext);
  return context?.fetchOutcomeData;
}

/**
 * Hook to check if data is loading for an outcome
 */
export function useTradingDataLoading(outcomeId: string): boolean {
  const context = useContext(TradingDataContext);
  return context?.isLoading(outcomeId) ?? false;
}

/**
 * Hook to get positions count for an outcome (based on balances)
 */
export function useTradingPositionsCount(outcomeId: string): number {
  const balances = useTradingBalances();
  const context = useContext(TradingDataContext);

  if (!context || !balances?.balances) return 0;

  // Find outcome by ID to get token IDs
  const outcome = context.outcomes.find((o) => o.id === outcomeId);
  if (!outcome) return 0;

  const resolutions = outcome.r;
  const yesTokenId = resolutions?.find((r) => r.isPositive === true)?.id;
  const noTokenId = resolutions?.find((r) => r.isPositive === false)?.id;

  // Count positions by filtering balances for this outcome's tokens with totalVolume > 0
  // Use totalVolume instead of value to include positions with frozen shares (TP/SL orders)
  const positionsCount = balances.balances.filter((balance) => {
    const isOutcomeToken =
      balance.eventTokenId === yesTokenId || balance.eventTokenId === noTokenId;
    const totalShares = balance.totalVolume ?? balance.value ?? 0;
    return isOutcomeToken && totalShares > 0;
  }).length;

  return positionsCount;
}

/**
 * Hook to get shares balance for a specific outcome and intent (YES/NO)
 * Replaces useSharesBalance - uses shared balances from TradingDataContext
 */
export function useTradingSharesBalance(
  outcomeId: string,
  intent: 'yes' | 'no',
): number | undefined {
  const balances = useTradingBalances();
  const context = useContext(TradingDataContext);

  if (!context || !balances?.balances) return undefined;

  const outcome = context.outcomes.find((o) => o.id === outcomeId);
  if (!outcome) return undefined;

  const resolutions = outcome.r;
  const yesResolution = resolutions?.find((r) => r.isPositive === true);
  const noResolution = resolutions?.find((r) => r.isPositive === false);
  const yesTokenId = yesResolution?.id;
  const noTokenId = noResolution?.id;

  // Find balance matching current intent
  const targetId = intent === 'yes' ? yesTokenId : noTokenId;
  const userBalance = balances.balances.find((balance) => balance.eventTokenId === targetId);

  return userBalance?.value ?? 0;
}

export function useTradingTotalSharesBalance(
  outcomeId: string,
  intent: 'yes' | 'no',
): number | undefined {
  const balances = useTradingBalances();
  const context = useContext(TradingDataContext);

  if (!context || !balances?.balances) return undefined;

  const outcome = context.outcomes.find((o) => o.id === outcomeId);
  if (!outcome) return undefined;

  const resolutions = outcome.r;
  const yesResolution = resolutions?.find((r) => r.isPositive === true);
  const noResolution = resolutions?.find((r) => r.isPositive === false);
  const yesTokenId = yesResolution?.id;
  const noTokenId = noResolution?.id;

  // Find balance matching current intent
  const targetId = intent === 'yes' ? yesTokenId : noTokenId;
  const userBalance = balances.balances.find((balance) => balance.eventTokenId === targetId);

  return userBalance?.totalVolume ?? 0;
}
